import React, { useState, useLayoutEffect, useMemo } from "react";
import cn from "classnames";
import style from "./common.module.scss";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { useTranslation } from "react-i18next";
import { useScreenSize } from "../../../hooks/useScreenSize";
import {
  formatDateWithDay,
  getAmChartsLocale,
  processDataByMonth,
  shouldShowYear,
} from "../../../utils/chartDataProcessors";
import { useLanguage } from "../../../context/LanguageContext";
import GroupByDropdown from "./GroupByDropdown/GroupByDropdown";
import SelectCurrencyOverlay from "./SelectCurrencyOverlay/SelectCurrencyOverlay";

const AnalyticsByTurnover = ({ item, currency }) => {
  const { t } = useTranslation();
  const { isMobileView } = useScreenSize();
  const { currentLanguage } = useLanguage();
  const [groupBy, setGroupBy] = useState("day");

  const processTurnoverData = (data, language, groupBy) => {
    const fallbackDate = new Date();
    const formattedFallbackDate = fallbackDate.toLocaleDateString("ru-RU").replace(/\//g, ".");

    if (!data || !Array.isArray(data) || data.length === 0) {
      return [
        {
          month: formattedFallbackDate,
          tender: 0,
          assigned: 0,
          sum: 0,
        },
      ];
    }

    if (groupBy === "day") {
      const showYear = shouldShowYear(data);

      return data
        .map((item) => {
          const [day, month, year] = item.date.split(".");
          const isoDate = new Date(year, month - 1, day);

          return {
            ...item,
            month: formatDateWithDay(isoDate, language, showYear),
            tooltipText: formatDateWithDay(isoDate, language, showYear).replace("\n", " "),
            isoDate,
            tender: item.tender || 0,
            assigned: item.assigned || 0,
            sum: item.sum || 0,
          };
        })
        .sort((a, b) => a.isoDate - b.isoDate);
    } else {
      const convertedData = data.map((item) => {
        const [day, month, year] = item.date.split(".");
        const formattedDate = `${day.padStart(2, "0")}.${month.padStart(2, "0")}.${year}`;

        return {
          date: formattedDate,
          tender: item.tender || 0,
          assigned: item.assigned || 0,
          sum: item.sum || 0,
        };
      });

      const numericFields = ["tender", "assigned", "sum"];
      return processDataByMonth(convertedData, language, numericFields);
    }
  };

  const processedData = useMemo(() => {
    return processTurnoverData(item, currentLanguage, groupBy);
  }, [item, currentLanguage, groupBy]);

  useLayoutEffect(() => {
    let root = am5.Root.new("chartdiv-turnover");
    root.setThemes([am5themes_Animated.new(root)]);

    const locale = getAmChartsLocale(currentLanguage);
    if (locale) {
      root.locale = locale;
    }

    root._logo.dispose();

    let chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        layout: root.verticalLayout,
        wheelY: "zoomX",
      }),
    );

    if (isMobileView) {
      chart.set("paddingLeft", 1);
    }
    chart.set("wheelable", true);

    let xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: "month",
        groupData: true,
        renderer: am5xy.AxisRendererX.new(root, {
          minGridDistance: groupBy === "day" ? 60 : 40,
          cellStartLocation: 0.1,
          cellEndLocation: 0.9,
        }),
        tooltip: am5.Tooltip.new(root, {
          labelText: groupBy === "day" ? "{tooltipText}" : "{month}",
        }),
      }),
    );
    xAxis.data.setAll(processedData);

    const cursor = chart.set(
      "cursor",
      am5xy.XYCursor.new(root, {
        behavior: "zoomX",
        xAxis: xAxis,
      }),
    );
    cursor.lineY.set("visible", false);

    xAxis.get("renderer").grid.template.setAll({
      strokeDasharray: [4, 4],
      strokeOpacity: 1,
      stroke: am5.color(0xe7e7f4),
    });

    if (groupBy === "day") {
      xAxis.get("renderer").labels.template.setAll({
        fill: am5.color("#8E8E8E"),
        fontSize: 12,
        rotation: 0,
        multiline: true,
        textAlign: "center",
        centerX: am5.percent(50),
        maxWidth: 70,
        oversizedBehavior: "wrap",
      });
    } else {
      xAxis.get("renderer").labels.template.setAll({
        fill: am5.color("#8E8E8E"),
        fontSize: 12,
        rotation: 0,
        multiline: true,
        textAlign: "center",
        centerX: am5.percent(50),
        maxWidth: 50,
        oversizedBehavior: "wrap",
      });
    }

    let yAxisLeft = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {}),
      }),
    );

    yAxisLeft.get("renderer").grid.template.setAll({
      strokeDasharray: [4, 4],
      strokeOpacity: 1,
      stroke: am5.color(0xe7e7f4),
    });

    yAxisLeft.get("renderer").labels.template.setAll({
      fill: am5.color("#8E8E8E"),
      fontSize: 12,
    });

    let yAxisRight = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {
          opposite: true,
        }),
      }),
    );

    yAxisRight.get("renderer").labels.template.setAll({
      forceHidden: true,
    });

    yAxisRight.get("renderer").ticks.template.setAll({
      visible: false,
    });

    yAxisRight.get("renderer").grid.template.setAll({
      visible: false,
    });

    const colors = {
      tender: am5.color(0xffb5ce),
      assigned: am5.color(0x4e7cff),
      sum: am5.color(0x4064ca),
    };

    let tenderSeries = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        name: t("analytics.tender"),
        xAxis: xAxis,
        yAxis: yAxisLeft,
        categoryXField: "month",
        valueYField: "tender",
        stacked: true,
      }),
    );

    tenderSeries.columns.template.setAll({
      fill: colors.tender,
      stroke: colors.tender,
      maxWidth: 80,
    });

    tenderSeries.set(
      "tooltip",
      am5.Tooltip.new(root, {
        labelText: "{name}: {valueY}",
      }),
    );

    tenderSeries.data.setAll(processedData);

    let assignedSeries = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        name: t("analytics.directPass"),
        xAxis: xAxis,
        yAxis: yAxisLeft,
        categoryXField: "month",
        valueYField: "assigned",
        stacked: true,
      }),
    );

    assignedSeries.columns.template.setAll({
      fill: colors.assigned,
      stroke: colors.assigned,
      maxWidth: 80,
    });

    assignedSeries.set(
      "tooltip",
      am5.Tooltip.new(root, {
        labelText: "{name}: {valueY}",
      }),
    );

    assignedSeries.data.setAll(processedData);

    let sumSeries = chart.series.push(
      am5xy.LineSeries.new(root, {
        name: t("analytics.turnoverMoney"),
        xAxis: xAxis,
        yAxis: yAxisRight,
        categoryXField: "month",
        valueYField: "sum",
      }),
    );

    sumSeries.strokes.template.setAll({
      stroke: colors.sum,
      strokeWidth: 2,
      strokeDasharray: [4, 4],
    });

    sumSeries.bullets.push(() => {
      return am5.Bullet.new(root, {
        sprite: am5.Circle.new(root, {
          radius: 4,
          fill: colors.sum,
          stroke: am5.color("#4064CA"),
          strokeWidth: 2,
        }),
      });
    });

    sumSeries.set(
      "tooltip",
      am5.Tooltip.new(root, {
        labelText: "{name}: {valueY}",
      }),
    );

    sumSeries.data.setAll(processedData);

    return () => {
      root.dispose();
    };
  }, [item, isMobileView, processedData, currentLanguage, groupBy]);

  return (
    <div className={style.content}>
      <div className={style.filter}>
        <span className={style.filter_title}>{t("analytics.turnover")}</span>
        <GroupByDropdown groupBy={groupBy} setGroupBy={setGroupBy} />
      </div>

      <div className={cn(style.area__namesMobile)}>
        <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
          <div className={style.area_icon} style={{ backgroundColor: "#FFB5CE" }}></div>
          <div className={style.area_type}>{t("analytics.tender")}</div>
        </div>
        <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
          <div className={style.area_icon} style={{ backgroundColor: "#4E7CFF" }}></div>
          <div className={style.area_type}>{t("analytics.directPass")}</div>
        </div>
        <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
          <div
            className={style.area_icon}
            style={{
              backgroundColor: "#FF6B35",
              borderRadius: "2px",
              background:
                "linear-gradient(90deg, #FF6B35 40%, transparent 40%, transparent 60%, #FF6B35 60%)",
            }}
          ></div>
          <div className={style.area_type}>{t("analytics.turnoverMoney")}</div>
        </div>
      </div>

      <div className={style.area} style={{ position: "relative" }}>
        <SelectCurrencyOverlay show={currency === "all" || currency === ""} />
        <div id="chartdiv-turnover" className={style.area_chart}></div>
        <div className={style.area_names}>
          <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
            <div className={style.area_icon} style={{ backgroundColor: "#FFB5CE" }}></div>
            <div className={style.area_type}>{t("analytics.tender")}</div>
          </div>
          <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
            <div className={style.area_icon} style={{ backgroundColor: "#4E7CFF" }}></div>
            <div className={style.area_type}>{t("analytics.directPass")}</div>
          </div>
          <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
            <svg width="44" height="16" viewBox="0 0 12 4" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="5.17911" cy="1.87686" r="1.47305" fill="#4064CA" />
              <path
                d="M0.759766 1.38545C0.488584 1.38545 0.268748 1.60528 0.268748 1.87646C0.268748 2.14765 0.488584 2.36748 0.759766 2.36748V1.87646V1.38545ZM0.759766 1.87646V2.36748H1.27533V1.87646V1.38545H0.759766V1.87646ZM2.30647 1.87646V2.36748H3.33761V1.87646V1.38545H2.30647V1.87646ZM4.36875 1.87646V2.36748H5.39989V1.87646V1.38545H4.36875V1.87646ZM6.43102 1.87646V2.36748H7.46216V1.87646V1.38545H6.43102V1.87646ZM8.4933 1.87646V2.36748H9.52444V1.87646V1.38545H8.4933V1.87646ZM10.5556 1.87646V2.36748H11.0711V1.87646V1.38545H10.5556V1.87646Z"
                fill="#4064CA" />
            </svg>
            <div className={style.area_type}>{t("analytics.turnoverMoney")}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsByTurnover;
